import { FormControlLabel, makeStyles } from '@material-ui/core'
import React from 'react'
import request from 'request-promise'
import Auth from '../Auth'
import Form from '../Form'
import PageStatus from '../PageStatus'
import pageStatusStyles from '../PageStatus.module.css'
import State from '../State'
import Student from '../Student'
import { collect, isHeg, isTicOrComem, isTinOrEcg } from '../lib/utils'
import baseStyles from './Base.module.css'
import HegComponent from './Lang/Heg.js'
import TicOrComemComponent from './Lang/TicOrComem.js'
import TinOrEcgComponent from './Lang/TinOrEcg.js'
import Equivalence from "./Lang/Equivalence";
import Allemand from './Lang/Allemand'
import Anglais from './Lang/Anglais'
import LangAccessCodes from "../LangAccessCodes";

const { REACT_APP_API_URI } = process.env

// #region Styles

const useStyles = makeStyles({
    root: {
        '& ul': {
            marginTop: '2rem',
        },
        '& li': {
            marginBottom: '2rem',
        },
        '& button': {
            display: 'block',
            marginTop: '1rem',
        },
    },
    choicesWrapper: {
        border: 'solid 1px #ddd',
        padding: '1.5rem',
        marginTop: '3rem',
        marginBottom: '4rem',
    },
    choices: {
        display: 'flex',
        flexDirection: 'row',
        '& > span': {
            minWidth: '30%',
            alignSelf: 'center',
        },
        '& label': {
            marginRight: '3rem',
            '&:last-of-type': {
                marginRight: 0,
            },
        },
        '& span': {
            fontSize: 'inherit',
        },
    },
    link: {
        display: 'block',
        marginTop: '1rem',
    },
    langAccessCodes: {
        marginTop: '1rem !important',
        '& li': {
            marginBottom: 0,
        },
        '& span': {
            display: 'inline-block',
            width: 150,
        },
    },
    instructions: {
        background: 'transparent',
        '& .MuiExpansionPanelDetails-root': {
            display: 'block',
        },
        '& li': {
            marginBottom: '.5rem',
        },
        '& li:last-of-type': {
            marginBottom: '2rem',
        },
    },
})

// #endregion

const updateForm =
    ({ token }) =>
    async (form) =>
        request({
            uri: `${REACT_APP_API_URI}/forms`,
            method: 'POST',
            body: form,
            json: true,
            headers: { authorization: `Bearer ${token}` },
        })

const getPageStatus = (student, form) => {
    const { requestEquivalence = undefined } = form || {}

    if (isHeg(student)) {
        return requestEquivalence === undefined
            ? {
                  done: false,
                  label: "Vous n'avez pas fait votre choix pour les demandes d'équivalence",
              }
            : { done: true, label: "Vous avez effectué votre choix pour les demandes d'équivalence" }
    } else {
        return requestEquivalence === undefined
            ? {
                  done: false,
                  label: "Vous n'avez pas fait votre choix pour la demande d'équivalence",
              }
            : { done: true, label: "Vous avez effectué votre choix pour la demande d'équivalence" }
    }
}

export default ({ path }) => {
    const styles = useStyles()
    const { student } = Student.useContainer()
    const { form, setForm } = Form.useContainer()
    const { token } = Auth.useContainer()
    const { langAccessCodes = {} } = LangAccessCodes.useContainer()

    const onChange =
        ({ key, value }) =>
        (e) => {
            const updatedForm = { ...form, [path]: { ...form[path], [key]: value } }
            setForm(updatedForm)
            updateForm({ token })(updatedForm)
        }

    const { done = false, label } = form && getPageStatus(student, form[path])

    const heg = isHeg(student)
    const ticOrComem = isTicOrComem(student)
    const tinOrEcg = isTinOrEcg(student)

    let showEnglish = false;
    let showAllemand = false;

    if (heg) {
      showEnglish = true;
      showAllemand = true;
    } else if (ticOrComem) {
      showEnglish = true;
      showAllemand = false;
    } else if (tinOrEcg) {
      showEnglish = student.languages === 'Anglais';
      showAllemand = student.languages === 'Allemand';
    }

    const props = {
        styles,
        form: form[path],
        onChange,
    }

    return (
        <div className={`${baseStyles.root} ${styles.root}`}>
            <h2>Vérification des niveaux de langues</h2>
            <PageStatus
              path={path}
              label={
                  heg
                    ? "J'ai passé les tests de langue en allemand et en anglais."
                    : "J'ai effectué mon test de langue."
              }
            >
                <p className={pageStatusStyles.title}>Statut des actions à entreprendre</p>
                <FormControlLabel
                    control={<State done={done} />}
                    label={label}
                    className={collect(pageStatusStyles.label, pageStatusStyles.noPointer)}
                />
              {showEnglish && <FormControlLabel
                control={<State done={langAccessCodes.oxford}/>}
                label={"Vous avez fait la demande pour les codes d'accès Oxford."}
                className={collect(pageStatusStyles.label, pageStatusStyles.noPointer)}
              />}
            </PageStatus>

            <h3>Informations générales</h3>
            <p>
                La maîtrise de l’allemand et de l’anglais est un atout essentiel pour votre avenir professionnel. Un bon niveau dans ces langues augmente vos opportunités sur le marché du travail. De plus, un niveau avancé peut vous offrir de nouvelles perspectives pendant vos études, comme l’accès à des cours supplémentaires, des échanges académiques ou des opportunités professionnelles.
            </p>
            {heg ? (
                <p>
                    Afin que chaque étudiant puisse progresser, plusieurs niveaux sont enseignés, du B1 au C1 en anglais, et du A1 au C1 en allemand. Il n’y a pas de cours de mise à niveau prévu.
                </p>
            ) : (
                <p>
                    Afin que chaque étudiant puisse progresser, plusieurs niveaux sont enseignés, du B1 au C1 en anglais, et du A2 au C1 en allemand. Il n’y a pas de cours de mise à niveau prévu.
                </p>
            )}
            <p>
                Le test de langues ci-dessous ainsi que votre profil linguistique nous permettront de vous placer dans une classe correspondant à votre niveau. Il est important que vous répondiez aux rubriques ci-dessous de manière autonome et individuelle, sans aide extérieure, et de manière sérieuse.
            </p>
            <p>Dernier délai : 30 juin</p>

            {showEnglish && <Anglais {...props} />}
            {showAllemand && <Allemand {...props} />}

            <h3>Equivalences</h3>
            <p>Si vous le souhaitez, vous pouvez demander une équivalence dans les situations suivantes :</p>
            <ul>
                <li>
                    Vous êtes titulaire d'un diplôme ouvrant droit à une équivalence (par exemple un C1 Advanced ou un BEC Higher de l’Université de Cambridge). L'attestation de niveau fournie par certains établissements gymnasiaux ou autres n'est pas reconnue comme un diplôme donnant lieu à une équivalence.
                </li>
                <li>
                    Vous avez suivi votre scolarité en anglais. Si vous êtes bilingue, des dispositions particulières s’appliquent.
                </li>
            </ul>
            <Equivalence {...props} />
        </div>
    )
}
